import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ValueSkeleton } from '@/components/ui/ValueSkeleton';
import clsx from 'clsx';
import { usePrefersReducedMotion } from '@/hooks/common/use-reduced-motion';
import {
  approxRateNote,
  formatExactAmount,
  type FooterColumnTotal,
} from '@/utils/trades/footer-totals';

interface MoneyMetricCellProps {
  label: string;
  /**
   * The currency-aware total for this metric. `total.text` is the compact value
   * (native `$1.2M` when single-ccy, `≈ €3.4M` when rolled up); the structured
   * `currencies`/`byCcy`/`equiv`/`base` drive the hover breakdown card.
   */
  total: FooterColumnTotal;
  /** Sign-convention/explanatory footnote shown at the bottom of the card. */
  formula?: string;
  /** Render the negative-exposure styling (red value + pulsing dot). */
  alert?: boolean;
  /** Blue fade flash when the active view changes (Trades MetricsBar only). */
  isFlashing?: boolean;
  /** Small pill after the value, e.g. "below MTA". */
  badge?: string;
  badgeTooltip?: string;
  /** No figure yet: a placeholder while it loads, a dash when it failed to; no card either way. */
  pending?: 'loading' | 'failed';
}

const CARD_WIDTH = 264;

/** Full-precision number (no currency code) for the card's per-currency rows. */
const fmtAmount = (value: number): string =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

/**
 * One money figure in the Trades/MTM metrics bar: a compact rolled-up value with
 * a portal hover-card that lists the full-precision per-currency breakdown (and
 * the `≈ base` equivalent when several currencies are present). Styled after the
 * Inventory "Lent to Counterparties" cell so both bars share one tooltip look.
 *
 * The breakdown is exposed to assistive tech via `aria-label` (no visual tooltip);
 * the portal card is the only thing sighted users see (LR-520).
 */
export const MoneyMetricCell: React.FC<MoneyMetricCellProps> = ({
  label,
  total,
  formula,
  alert = false,
  isFlashing = false,
  badge,
  badgeTooltip,
  pending,
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const shouldFlash = isFlashing && !prefersReducedMotion;
  const containerRef = useRef<HTMLDivElement>(null);
  const [showCard, setShowCard] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const { currencies, byCcy, equiv, base } = total;
  const multi = currencies.length > 1;

  const openCard = (): void => {
    if (pending || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    // Clamp to the viewport so a cell near the right edge doesn't overflow.
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - CARD_WIDTH - 8));
    setPos({ top: rect.bottom + 8, left });
    setShowCard(true);
  };

  // LR-474 r5 #5: the card is position:fixed at coordinates snapshotted when it opens,
  // so a viewport change (window resize, devtools dock, orientation) would strand it at
  // a stale anchor. The metrics bar lives OUTSIDE the table's own scroll container, so a
  // normal table scroll never moves the cell — but dismiss on resize/scroll defensively
  // so an orphaned card can never linger. No listeners are attached while the card is
  // closed (the effect early-returns), so there is no idle cost.
  useEffect(() => {
    if (!showCard) return;
    const dismiss = (): void => setShowCard(false);
    window.addEventListener('resize', dismiss);
    window.addEventListener('scroll', dismiss, true);
    return () => {
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('scroll', dismiss, true);
    };
  }, [showCard]);

  return (
    <div
      ref={containerRef}
      // Inset outline: the metrics bars scroll, and a scroll region clips anything drawn outside it.
      className="flex items-center gap-2 px-4 py-2.5 cursor-default focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-blue-500"
      // LR-520: a native `title` here rendered a second tooltip that overlapped the portal
      // hover-card — on a normal mouse hover the browser shows `title` AT THE SAME TIME
      // onMouseEnter opens the card, so both windows appeared with identical data. Expose
      // the breakdown to AT via `aria-label` (announced on focus, no visual tooltip) so the
      // portal card is the single visible tooltip. Keyboard/SR users still reach the rich
      // card via the tab stop (onFocus → openCard). `total.title` is '' for an empty cell.
      // `role="group"` (LR-550 review #2): a bare div is `generic`, which does not support
      // naming — the `aria-label` above would not be exposed to AT at all without a role
      // that allows it.
      role="group"
      tabIndex={0}
      aria-label={pending === 'failed' ? `${label}: could not load` : total.title || undefined}
      onMouseEnter={openCard}
      onMouseLeave={() => setShowCard(false)}
      onFocus={openCard}
      onBlur={() => setShowCard(false)}
    >
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide whitespace-nowrap">
        {label}
      </span>
      {pending === 'loading' ? (
        <ValueSkeleton className="h-4 w-20" />
      ) : pending === 'failed' ? (
        <span aria-hidden="true" className="text-sm font-mono text-slate-400">
          —
        </span>
      ) : (
        <div className="flex items-center">
          <span
            className={clsx(
              // whitespace-nowrap: keep a signed compact value like "-£2.0K" on one line.
              'text-sm font-semibold font-mono tabular-nums transition-opacity whitespace-nowrap',
              alert ? 'text-red-600' : 'text-slate-900',
              shouldFlash && 'animate-[fadeInOut_0.6s_ease-in-out]',
            )}
          >
            {total.text}
          </span>
          {total.approx && (
            <span
              className="ml-1 text-xs text-amber-600"
              role="img"
              aria-label={approxRateNote(total.approxCurrencies)}
            >
              ⚠
            </span>
          )}
          {alert && (
            // No `title` here: the container already runs a JS hover-card (LR-520) that
            // stays open while the cursor is anywhere inside it, including over this dot —
            // a native title tooltip would pop up on top of that card (the exact overlap
            // LR-520 fixed). `role="img"` + `aria-label` alone still fixes the a11y gap.
            <span
              className="w-2 h-2 rounded-full bg-red-500 inline-block ml-1.5 motion-safe:animate-pulse"
              role="img"
              aria-label="Negative exposure — against us"
            />
          )}
          {badge && (
            <>
              <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 whitespace-nowrap">
                {badge}
              </span>
              {/* Same LR-520 reason as the alert dot: no `title` on a node inside the
                  hover-card container. Sighted users get the explanation as a card footnote
                  below; AT gets it here. It cannot be an `aria-label` on the badge span —
                  that span is `generic`, which does not support naming (the LR-550 defect),
                  and the label would also have to restate the visible "below MTA" text. */}
              {badgeTooltip && <span className="sr-only">{badgeTooltip}</span>}
            </>
          )}
        </div>
      )}

      {showCard && !pending &&
        createPortal(
          <div
            className="fixed z-[9999] min-w-[240px] bg-white border border-gray-200 shadow-xl rounded-lg overflow-hidden p-4"
            style={{ top: `${pos.top}px`, left: `${pos.left}px`, width: `${CARD_WIDTH}px` }}
          >
            <h4 className="text-sm font-semibold text-blue-600 mb-3">{label}</h4>
            <dl className="space-y-2 text-xs">
              {currencies.length > 0 ? (
                currencies.map((ccy) => (
                  <div key={ccy} className="flex justify-between gap-6">
                    <dt className="text-gray-500">{ccy}:</dt>
                    <dd className="text-gray-900 font-medium font-mono tabular-nums text-right">
                      {fmtAmount(byCcy[ccy])}
                    </dd>
                  </div>
                ))
              ) : (
                <div className="flex justify-between gap-6">
                  <dt className="text-gray-500">Total:</dt>
                  <dd className="text-gray-900 font-medium font-mono tabular-nums text-right">{total.text}</dd>
                </div>
              )}
              {multi && (
                <div className="flex justify-between gap-6 border-t border-gray-200 pt-2 mt-1">
                  <dt className="text-gray-500">≈ {base}:</dt>
                  <dd className="text-gray-900 font-semibold font-mono tabular-nums text-right">
                    {formatExactAmount(equiv, base)}
                  </dd>
                </div>
              )}
            </dl>
            {total.approx && (
              <p className="mt-3 text-[11px] text-amber-600">{approxRateNote(total.approxCurrencies)}</p>
            )}
            {/* The badge's explanation lives here rather than in a `title` on the badge
                itself — the card is the only visible tooltip in this cell (LR-520). */}
            {badgeTooltip && <p className="mt-3 text-[11px] text-amber-700">{badgeTooltip}</p>}
            {formula && (
              <p className="mt-3 border-t border-gray-100 pt-2 text-[11px] text-gray-500 whitespace-pre-line">
                {formula}
              </p>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};
